import Vue from 'vue';
import VueRouter, { RouteConfig } from 'vue-router';
import Home from '@/views/home/home.vue';
import Connections from '@/views/connections/connections.vue';
import CompanyInfo from '@/views/company-info/company-info.vue';
import Billing from '@/views/billing/billing.vue';
import AdminCompanies from '@/views/admin-companies/admin-companies.vue';
import AdminCompanyOverview from '@/views/admin-company-overview/admin-company-overview.vue';
import AdminBilling from '@/views/admin-billing/admin-billing.vue';
import Register from '@/views/register/register.vue';
import SignIn from '@/views/sign-in/sign-in.vue';
import Settings from '@/views/settings/settings.vue';
import ResetPassword from '@/views/reset-password/reset-password.vue';
import ForgottenPassword from '@/views/forgotten-password/forgotten-password.vue';
import AdminUsers from '@/views/admin-users/admin-users.vue';

import { guards, withRoleGuard } from '@/functions/router-guards';
import { withCompanyIdProp, withRouteProps } from './props-helpers';

Vue.use(VueRouter);

export enum StepsEditorPhase
{
    edit,

    creation
}

const routes: Array<RouteConfig> = [
    /*
        Admin routes
    */
    {
        path: '/admin-management',
        name: 'Admin',
        component: () => import('../views/admin/admin.vue'),
        meta: {
            fileSystem: false
        },
        beforeEnter: withRoleGuard('Administrator', 'Moderator', 'ContentWriter', 'Economy', 'InternalSales'),
        children: [
            {
                path: 'imported-jobs',
                name: 'Admin Imported Jobs',
                component: () => import('../views/admin-imported-jobs/admin-imported-jobs.vue'),
                meta: { fileSystem: false, allowSmallScreen: true },
                beforeEnter: withRoleGuard('Administrator')
            },
            {
                path: ':company/partial-sponsoring/:id?',
                name: 'PartialSponsoring',
                component: () => import('../views/admin-partial-sponsoring/admin-partial-sponsoring.vue'),
                meta: {
                    fileSystem: false
                },
                beforeEnter: withRoleGuard('Administrator', 'Moderator')
            },
            {
                path: 'visibility-percentage-profile',
                name: 'Profile Visibility Percentage',
                component: () => import('../views/visibility-percentage-profile/visibility-percentage-profile.vue'),
                meta: {
                    fileSystem: false
                },
                beforeEnter: withRoleGuard('Administrator')
            },
            {
                path: 'visibility-percentage-volume',
                name: 'Volume Visibility Percentage',
                component: () => import('../views/visibility-percentage-volume/visibility-percentage-volume.vue'),
                meta: {
                    fileSystem: false
                },
                beforeEnter: withRoleGuard('Administrator')
            },
            {
                path: 'manage-promotions',
                name: 'Admin Manage promotions',
                component: () => import('../views/admin-manual-sponsored-postings/admin-manual-sponsored-postings.vue'),
                meta: {
                    fileSystem: false,
                    ad: false,
                },
                beforeEnter: withRoleGuard('Administrator')
            },
            {
                path: 'companies',
                name: 'Admin Companies',
                component: AdminCompanies,
                meta: {
                    fileSystem: false
                },
                beforeEnter: withRoleGuard('Administrator', 'Moderator', 'Economy', 'InternalSales')
            },
            {
                // Fix props to route params Edit/Create
                path: 'users',
                name: 'Admin Users',
                component: AdminUsers,
                meta: {
                    fileSystem: false
                },
                beforeEnter: withRoleGuard('Administrator', 'Moderator', 'InternalSales', 'ContentWriter', 'Economy')
            },
            {
                // Fix props to route params Edit/Create
                path: 'billing',
                name: 'Admin Billing',
                component: AdminBilling,
                props: true,
                meta: {
                    fileSystem: false
                },
                beforeEnter: withRoleGuard('Administrator', 'Moderator', 'Economy')
            },
            {
                // Fix props to route params Edit/Create
                path: 'companies/:company/:invoice?',
                name: 'Admin Company Overview',
                component: AdminCompanyOverview,
                props: true,
                meta: {
                    fileSystem: false
                },
                beforeEnter: withRoleGuard('Administrator', 'Moderator', 'Economy', 'InternalSales')
            },
        ]
    },
    /*
        Admin routes End
    */
    {
        path: '/:company/connections',
        name: 'Connections',
        component: Connections,
        props: withCompanyIdProp,
    },
    {
        path: '/:company/company-info',
        name: 'Company Info',
        component: CompanyInfo,
        meta: {
            fileSystem: false
        }
    },
    {
        path: '/:company/billing',
        name: 'Billing',
        component: Billing,
        meta: {
            fileSystem: false
        }
    },
    {
        path: '/register',
        name: 'User Registration',
        component: Register,
        meta: {
            fileSystem: false,
            anonymous: true
        },
        props: true
    },
    {
        path: '/sign-in',
        name: 'Sign In',
        component: SignIn,
        props: (route) =>
        {
            return { message: 'message' in route.query ? route.query.message : route.params.message };
        },
        meta: {
            fileSystem: false,
            anonymous: true
        }
    },
    {
        path: '/:company/settings',
        name: 'Settings Page',
        component: Settings,
        meta: {
            fileSystem: false
        }
    },
    {
        path: '/reset-password/:token',
        name: 'Reset Password',
        component: ResetPassword,
        meta: {
            fileSystem: false,
            anonymous: true
        }
    },
    {
        path: '/forgotten-password',
        name: 'Forgotten Password',
        component: ForgottenPassword,
        meta: {
            fileSystem: false,
            anonymous: true
        }
    },
    {
        path: '/assignment',
        name: 'Assignment',
        component: () => import('../views/assignment/assignment.vue'),
        meta: {
            anonymous: true,
            anonymousOnly: false
        },
    },
    {
        path: '/',
        name: 'Home',
        component: Home,
        props: true
    },
];

const router = new VueRouter({
    mode: 'history',
    base: process.env.BASE_URL,
    routes
});

router.beforeEach(guards);

export default router;
